import AppForms from "@toothfairy/shared-ui/AppForms";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import Constants from "../../../../Constants";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import IdCopyView from "../../../../Components/ToothFairyComponents/IdCopyView";
import { AppText } from "@toothfairy/shared-ui";
import {
  BuilderSection,
  PassthroughSection,
  useBuilderDirtyTracking,
} from "./BuilderSections";

const hosting_types = [
  {
    id: "ollama",
    name: "Ollama (Desktop client only)",
  },

  {
    id: "anthropic",
    name: "Anthropic",
  },
  {
    id: "azure_openai",
    name: "Azure OpenAI",
  },
  {
    id: "openai",
    name: "OpenAI",
  },
  {
    id: "google",
    name: "Google",
  },
  {
    id: "aws",
    name: "AWS Bedrock",
    enterprise: true,
  },
  {
    id: "local",
    name: "Private server",
    enterprise: true,
  },
];

const HOSTING_SECTION_FIELD_MAP = {
  profile: ["name", "description", "type"],
  credentials: ["tokenSecret"],
  endpoint: ["endpoint", "apiVersion", "modelName", "baseModel", "customModel"],
};

const HostingSettingsAIView = ({
  isEdit,
  settingType,
  handleDeletion,
  isSaveInProgress,
  onUpdateSecret,
  isUpdatingSecret,
  isBuilderLayout,
  builderActiveSection,
  onBuilderContextChange,
  saveVersion,
}) => {
  const { t } = useTranslation();
  const { currentTheme, mode } = AppTheme.useTheme();
  const { getPlanConfig, getActiveWorkspace } = useWorkspaces();
  const entityName = t("settings.hosting.entityName");
  const {
    setFieldValue,
    values,
    validationSchema,
    initialValues,
    errors,
    submitCount,
  } = AppForms.useFormikContext();
  const [initialLoadCompleted, setInitialLoadCompleted] = React.useState(false);
  const _title = useCallback(() => {
    return isEdit ? t("common.save") : t("common.create");
  }, [entityName, isEdit]);

  const _showBuilderSection = (sectionId) =>
    builderActiveSection == null || builderActiveSection === sectionId;
  const SectionRenderer = isBuilderLayout ? BuilderSection : PassthroughSection;

  useBuilderDirtyTracking({
    isBuilderLayout,
    values,
    initialValues,
    saveVersion,
    onBuilderContextChange,
    errors,
    submitCount,
    sectionFieldMap: HOSTING_SECTION_FIELD_MAP,
  });

  // The container builds the sidebar sections from the CURRENT provider type
  // (e.g. Credentials only apply to key-based providers, Endpoint only to
  // endpoint-based ones). `type` is a live form dropdown, so push the live
  // value up on every change — the baked-in initial `initialValues.type`
  // ("ollama") must not gate what sections the user can reach.
  const contextType = values?.type;
  const prevContextTypeRef = React.useRef(contextType);
  useEffect(() => {
    if (prevContextTypeRef.current === contextType) return;
    prevContextTypeRef.current = contextType;
    onBuilderContextChange?.({ isDirty: true, contextType });
  }, [contextType, onBuilderContextChange]);

  const handleUpdateSecretClick = useCallback(() => {
    setFieldValue("tokenSecret", "");
    onUpdateSecret();
  }, [setFieldValue, onUpdateSecret]);

  useEffect(() => {
    setTimeout(() => {
      setInitialLoadCompleted(true);
    }, [2000]);
  }, []);

  useEffect(() => {
    if (values?.customModel && initialLoadCompleted) {
      setFieldValue("baseModel", "mistralai/Mistral-7B-Instruct-v0.1");
    } else {
      setFieldValue("baseModel", null);
    }
  }, [values?.customModel, initialLoadCompleted]);

  return (
    <>
      {_showBuilderSection("profile") && (
        <SectionRenderer
          title={t("hostings.builderSectionProfile", {
            defaultValue: "Overview",
          })}
          icon={AppAssets.icons.persona}
        >
          {isEdit && <IdCopyView id={values?.id} label="ID" />}
          <AppForms.AppFormField
            showTextInputLabel
            isMandatory
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            label={t("settings.hosting.providerName")}
            placeholder={t("settings.hosting.providerNamePlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="name"
          />
          <AppForms.AppFormField
            showTextInputLabel
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            multiline
            textContainer={{
              minHeight: 120,
              paddingVertical: 10,
              marginHorizontal: 5,
            }}
            label={t("settings.hosting.descriptionLabel")}
            placeholder={t("settings.hosting.descriptionPlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="description"
          />
          <AppForms.AppFormDropDown
            isElevated={false}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
              maxWidth: !isLargeScreen() && 250,
            }}
            showTextInputLabel
            label={t("common.type")}
            isMandatory
            name="type"
            items={hosting_types
              .filter((item) => {
                if (
                  !item.enterprise ||
                  (item.enterprise &&
                    (getPlanConfig()?.isEnterprise ||
                      getActiveWorkspace()?.enableExperimentalFeatures == true))
                ) {
                  return item;
                }
              })
              .filter((item) => item)}
            propertyLabel="name"
            propertyValue="id"
          />
        </SectionRenderer>
      )}
      {_showBuilderSection("credentials") &&
        !["none", "local", "ollama"].includes(values?.type) && (
          <SectionRenderer
            title={t("hostings.builderSectionCredentials", {
              defaultValue: "Credentials",
            })}
            icon={AppAssets.icons.credentials}
          >
            <View style={{ position: "relative" }}>
              <AppForms.AppFormField
                showTextInputLabel
                isMandatory
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                disabled={isEdit && !isUpdatingSecret}
                label={t("settings.hosting.tokenLabel")}
                placeholder={
                  isEdit && !isUpdatingSecret
                    ? t("settings.hosting.apiKeyCannotBeViewed")
                    : isEdit && isUpdatingSecret
                    ? t("settings.hosting.enterNewApiKey")
                    : t("settings.hosting.apiKeyPlaceholder")
                }
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name={isEdit && !isUpdatingSecret ? "$xc" : "tokenSecret"}
              />
              {isEdit && !isUpdatingSecret && (
                <ToothFairyButton
                  title={t("settings.hosting.updateApiKey")}
                  onPress={handleUpdateSecretClick}
                  style={{
                    height: 25,
                    paddingHorizontal: 0,
                  }}
                  btnContentStyle={{
                    paddingHorizontal: 5,
                  }}
                  backgroundColor={currentTheme?.theme?.soft_primary}
                  wrapperStyle={{
                    position: "absolute",
                    right: 0,
                    top: 49,
                    minWidth: 120,
                    height: 25,
                    zIndex: 10,
                  }}
                />
              )}
            </View>
          </SectionRenderer>
        )}
      {_showBuilderSection("endpoint") && (
        <SectionRenderer
          title={t("hostings.builderSectionEndpoint", {
            defaultValue: "Endpoint",
          })}
          icon={AppAssets.icons.hosting}
        >
          {(values?.type == "local" || values?.type == "ollama") && (
            <View
              style={{
                zIndex: -1,
              }}
            >
              <AppForms.AppFormField
                showTextInputLabel
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                isMandatory
                label={t("settings.hosting.endpointLabel")}
                placeholder={t("settings.hosting.localEndpointPlaceholder")}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="endpoint"
              />
            </View>
          )}
          {values?.type == "azure_openai" && (
            <View
              style={{
                zIndex: -1,
              }}
            >
              <AppForms.AppFormField
                showTextInputLabel
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                isMandatory
                label={t("settings.hosting.endpointLabel")}
                placeholder={t("settings.hosting.azureEndpointPlaceholder")}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="endpoint"
              />
              <AppForms.AppFormField
                showTextInputLabel
                isMandatory
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                label={t("settings.hosting.apiVersionLabel")}
                placeholder={t("settings.hosting.apiVersionPlaceholder")}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name={"apiVersion"}
              />
            </View>
          )}
          {(values?.type == "azure_openai" || values?.type == "local") && (
            <View>
              <AppForms.AppFormField
                showTextInputLabel
                isMandatory
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                label={t("settings.hosting.modelNameLabel")}
                placeholder={t("settings.hosting.modelNamePlaceholder")}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="modelName"
              />
            </View>
          )}
          {values?.type == "ollama" && (
            <View style={{ zIndex: -1 }}>
              <AppText
                leftIcon={AppAssets?.icons?.info}
                wrapperStyle={{
                  paddingHorizontal: 5,
                }}
                color={currentTheme?.theme?.orange}
                style={{
                  marginLeft: 10,
                }}
                iconSize={14}
                preventTranslation
              >
                {t("settings.hosting.ollamaInfoText")}{" "}
                <AppText
                  onPress={() =>
                    window.open("https://toothfairyai.com/download", "_blank")
                  }
                  isClickable
                  style={{
                    textDecorationLine: "underline",
                    cursor: "pointer",
                  }}
                  color={currentTheme?.theme?.primary}
                >
                  {t("settings.hosting.downloadDesktopClient")}
                </AppText>
              </AppText>
              <AppText
                leftIcon={AppAssets?.icons?.info}
                wrapperStyle={{
                  paddingHorizontal: 5,
                  marginTop: 10,
                }}
                color={currentTheme?.theme?.orange}
                style={{
                  marginLeft: 10,
                }}
                iconSize={14}
              >
                {t("settings.hosting.ollamaAutoRetrievalText")}
              </AppText>
            </View>
          )}
        </SectionRenderer>
      )}
      {!isBuilderLayout && (
        <View style={{ alignItems: "center" }}>
          <AppForms.AppSubmitButton
            title={_title()}
            awesomeIconColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.primary}
            isDisabled={isSaveInProgress}
            btnContentStyle={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            style={[
              {
                height: Constants.appButtonHeight,
                width: "auto",
                shadowColor: currentTheme?.theme?.transparent,
                marginHorizontal: 10,
              },
            ]}
            // btnLabelStyle={{ fontSize: 16 }}
            wrapperStyle={[
              {
                width: "fit-content",
                minWidth: 150,
                marginTop: 20,
                height: Constants.appButtonHeight,
              },
            ]}
          />
          {isEdit && (
            <ToothFairyButton
              title={t("common.delete")}
              onPress={handleDeletion}
              isDeletion
              wrapperStyle={{
                marginTop: 30,
                minWidth: 150,
                height: Constants.appButtonHeight,
              }}
            />
          )}
        </View>
      )}
    </>
  );
};

export default HostingSettingsAIView;
